import { api } from '../client'

export interface Tenant {
  id: string
  name: string
  subdomain: string
  isActive: boolean
  planType: string
  userCount: number
  templateCount: number
  assetCount: number
  storageUsedBytes: number
  storageLimitBytes: number
  createdAt: string
  updatedAt: string
}

export interface TenantListResponse {
  items: Tenant[]
  pageNumber: number
  totalPages: number
  totalCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

export interface CreateTenantRequest {
  name: string
  subdomain: string
  planType: string
  adminEmail?: string
  adminFirstName?: string
  adminLastName?: string
  adminPassword?: string
}

export interface UpdateTenantRequest {
  id: string
  name: string
  planType?: string
  isActive?: boolean
}

export interface SubdomainCheckResult {
  subdomain: string
  isAvailable: boolean
  message: string
}

export const tenantsService = {
  /**
   * Get all tenants with pagination and filtering (requires super admin)
   */
  async getTenants(params?: {
    pageNumber?: number
    pageSize?: number
    searchTerm?: string
    isActive?: boolean
    planType?: string
    sortBy?: string
    sortDescending?: boolean
  }): Promise<TenantListResponse> {
    const response = await api.get<TenantListResponse>('/v1/tenants', { params })
    return response.data
  },

  /**
   * Get a tenant by ID (requires super admin)
   */
  async getTenant(id: string): Promise<Tenant> {
    const response = await api.get<Tenant>(`/v1/tenants/${id}`)
    return response.data
  },

  /**
   * Create a new tenant (requires super admin)
   */
  async createTenant(data: CreateTenantRequest): Promise<Tenant> {
    const response = await api.post<Tenant>('/v1/tenants', data)
    return response.data
  },

  /**
   * Update a tenant (requires super admin)
   */
  async updateTenant(id: string, data: UpdateTenantRequest): Promise<void> {
    await api.put(`/v1/tenants/${id}`, data)
  },

  /**
   * Delete (deactivate) a tenant (requires super admin)
   */
  async deleteTenant(id: string, forceDelete: boolean = false): Promise<void> {
    await api.delete(`/v1/tenants/${id}`, { params: { forceDelete } })
  },

  /**
   * Check if a subdomain is available
   */
  async checkSubdomain(subdomain: string): Promise<SubdomainCheckResult> {
    const response = await api.get<SubdomainCheckResult>(`/v1/tenants/check-subdomain/${subdomain}`)
    return response.data
  },

  /**
   * Get tenant dashboard with detailed stats
   */
  async getTenantDashboard(id: string): Promise<TenantDashboard> {
    const response = await api.get<TenantDashboard>(`/v1/tenants/${id}/dashboard`)
    return response.data
  },
}

// Dashboard types
export interface TenantDashboard {
  tenant: TenantInfo
  storage: StorageStats
  usage: UsageStats
  topUsers: TenantUser[]
  topTemplates: TenantTemplate[]
  recentAssets: TenantAsset[]
  teams: TenantTeam[]
}

export interface TenantInfo {
  id: string
  name: string
  subdomain: string
  isActive: boolean
  planType: string
  createdAt: string
  updatedAt: string
  companyEmail?: string
  companyPhone?: string
  companyWebsite?: string
  companyAddress?: string
  companyCity?: string
  companyState?: string
  companyCountry?: string
  companyPostalCode?: string
  billingEmail?: string
  billingAddress?: string
  taxId?: string
}

export interface StorageStats {
  usedBytes: number
  limitBytes: number
  usagePercentage: number
  totalFiles: number
  averageFileSize: number
}

export interface UsageStats {
  totalUsers: number
  activeUsers: number
  totalTemplates: number
  publishedTemplates: number
  totalAssets: number
  totalDocumentsGenerated: number
  lastActivityAt?: string
}

export interface TenantUser {
  id: string
  email: string
  fullName: string
  avatarUrl?: string
  isActive: boolean
  lastLoginAt?: string
  role: string
}

export interface TenantTemplate {
  id: string
  name: string
  type: string
  category?: string
  usageCount: number
  createdAt: string
  updatedAt: string
}

export interface TenantAsset {
  id: string
  name: string
  type: string
  size: number
  thumbnailUrl?: string
  createdAt: string
}

export interface TenantTeam {
  id: string
  name: string
  description?: string
  color?: string
  memberCount: number
  isActive: boolean
  createdAt: string
}
